import React from 'react';
import { Table } from '@tanstack/react-table';

interface TanStackPaginationControlsProps<T> {
    table: Table<T>;
}

const TanStackPaginationControls = <T,>({ table }: TanStackPaginationControlsProps<T>) => {
    const pagination = table.getState().pagination;
    const totalRows = table.getRowCount();
    const currentPage = pagination.pageIndex + 1;
    const totalPages = table.getPageCount();
    const pageSize = pagination.pageSize;

    const startRow = pagination.pageIndex * pageSize + 1;
    const endRow = Math.min((pagination.pageIndex + 1) * pageSize, totalRows);

    if (totalRows === 0) return null;

    return (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            {/* Mobile view */}
            <div className="flex-1 flex justify-between sm:hidden">
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                    Anterior
                </button>
                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                    Próximo
                </button>
            </div>

            {/* Desktop view */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center space-x-4">
                    <p className="text-sm text-gray-700">
                        Mostrando{' '}
                        <span className="font-medium">{startRow}</span>
                        {' '}a{' '}
                        <span className="font-medium">{endRow}</span>
                        {' '}de{' '}
                        <span className="font-medium">{totalRows}</span>
                        {' '}resultados
                    </p>

                    {/* Page size selector */}
                    <div className="flex items-center space-x-2">
                        <label htmlFor="pageSize" className="text-sm text-gray-700">
                            Itens por página:
                        </label>
                        <select
                            id="pageSize"
                            value={pageSize}
                            onChange={e => table.setPageSize(Number(e.target.value))}
                            className="border border-gray-300 rounded-md text-sm pl-2 pr-8 py-1"
                        >
                            {[5, 10, 20, 30, 50].map(size => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    {/* Navigation buttons */}
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                            onClick={() => table.firstPage()}
                            disabled={!table.getCanPreviousPage()}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            title="Primeira página"
                        >
                            {'<<'}
                        </button>
                        <button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            title="Página anterior"
                        >
                            {'<'}
                        </button>

                        {/* Page indicator */}
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-gray-50 text-sm font-medium text-gray-700">
                            Página {currentPage} de {totalPages}
                        </span>

                        <button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            title="Próxima página"
                        >
                            {'>'}
                        </button>
                        <button
                            onClick={() => table.lastPage()}
                            disabled={!table.getCanNextPage()}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            title="Última página"
                        >
                            {'>>'}
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );
};

export default TanStackPaginationControls; 